import { assert, assertNever } from '@sparkdotfi/common-universal'
import { useChainId } from 'wagmi'

import { ApproveDelegationAction } from '../flavours/approve-delegation/types'
import { BorrowAction } from '../flavours/borrow/types'
import { ClaimMarketRewardsAction } from '../flavours/claim-market-rewards/types'
import { DepositAction } from '../flavours/deposit/types'
import { createDepositToSavingsActions } from '../flavours/deposit-to-savings/logic/createDepositToSavingsActions'
import { PermitAction } from '../flavours/permit/types'
import { RepayAction } from '../flavours/repay/types'
import { SetUseAsCollateralAction } from '../flavours/set-use-as-collateral/types'
import { SetUserEModeAction } from '../flavours/set-user-e-mode/logic/types'
import { StakeSpkAction } from '../flavours/stake-spk/types'
import { createSwapActions } from '../flavours/swap/logic/createSwapActions'
import { UpgradeSdaiAction } from '../flavours/upgrade-sdai/types'
import { createUpgradeSusdcActions } from '../flavours/upgrade-susdc/logic/createUpgradeSusdcActions'
import { WithdrawAction } from '../flavours/withdraw/types'
import { createWithdrawFromSavingsActions } from '../flavours/withdraw-from-savings/logic/createWithdrawFromSavingsActions'
import { getApproveActions } from './getApproveActions'
import { Action, ActionContext, Objective } from './types'
import {
  lendingPoolAddress,
  migrationActionsConfig,
  spkStakingModuleConfig,
  wethGatewayAddress,
} from '@/config/contracts-generated'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export interface UseCreateActionsParams {
  objectives: Objective[]
  context: ActionContext
  batchingEnabled: boolean
}

export function useCreateActions({ objectives, context, batchingEnabled }: UseCreateActionsParams): Action[] {
  const chainConfig = useChainConfigEntry()
  const chainId = useChainId()
  const _actionSettings = context.actionSettings
  assert(_actionSettings, 'actionsSettings is required')
  const actionsSettings = {
    ..._actionSettings,
    preferPermits: batchingEnabled ? false : _actionSettings.preferPermits,
  }

  function getNativeAssetInfoSymbol(): TokenSymbol {
    return chainConfig.nativeAssetInfo.nativeAssetSymbol
  }

  return objectives.flatMap((objective): Action[] => {
    // @note: you can create hooks (actions) conditionally, but ensure that component will be re-mounted when condition changes
    // to accomplish this, tweak stringifyObjectivesToStableActions function

    switch (objective.type) {
      case 'deposit': {
        const lendingPool = getContractAddress(lendingPoolAddress, chainId)

        const depositAction: DepositAction = {
          type: 'deposit',
          token: objective.token,
          value: objective.value,
        }

        if (objective.token.symbol === getNativeAssetInfoSymbol()) {
          return [depositAction]
        }

        if (actionsSettings.preferPermits && chainConfig.permitSupport[objective.token.address]) {
          const permitAction: PermitAction = {
            type: 'permit',
            token: objective.token,
            spender: lendingPool,
            value: objective.value,
          }

          return [permitAction, depositAction]
        }

        return [
          ...getApproveActions({
            token: objective.token,
            spender: lendingPool,
            value: objective.value,
            context,
          }),
          depositAction,
        ]
      }

      case 'withdraw': {
        const wethGateway = getContractAddress(wethGatewayAddress, chainId)

        if (objective.reserve.token.symbol === getNativeAssetInfoSymbol()) {
          assert(objective.gatewayApprovalValue, 'gatewayApprovalValue is required for native asset')
          const approveActions = getApproveActions({
            token: objective.reserve.aToken,
            spender: wethGateway,
            value: objective.gatewayApprovalValue,
            context,
          })
          const withdrawAction: WithdrawAction = {
            type: 'withdraw',
            token: objective.reserve.token,
            value: objective.value,
            all: objective.all,
          }
          return [...approveActions, withdrawAction]
        }

        const withdrawAction: WithdrawAction = {
          type: 'withdraw',
          token: objective.reserve.token,
          value: objective.value,
          all: objective.all,
        }
        return [withdrawAction]
      }

      case 'borrow': {
        if (objective.token.symbol === getNativeAssetInfoSymbol()) {
          const approveDelegationAction: ApproveDelegationAction = {
            type: 'approveDelegation',
            token: objective.token,
            value: objective.value,
          }

          const borrowAction: BorrowAction = {
            type: 'borrow',
            token: objective.token,
            value: objective.value,
          }
          return [approveDelegationAction, borrowAction]
        }

        const borrowAction: BorrowAction = {
          type: 'borrow',
          token: objective.token,
          value: objective.value,
        }
        return [borrowAction]
      }

      case 'repay': {
        const lendingPool = getContractAddress(lendingPoolAddress, chainId)

        const repayAction: RepayAction = {
          type: 'repay',
          reserve: objective.reserve,
          value: objective.value,
          useAToken: objective.useAToken,
        }

        if (objective.reserve.token.symbol === getNativeAssetInfoSymbol() || objective.useAToken) {
          return [repayAction]
        }

        if (actionsSettings.preferPermits && chainConfig.permitSupport[objective.reserve.token.address]) {
          const permitAction: PermitAction = {
            type: 'permit',
            token: objective.reserve.token,
            spender: lendingPool,
            value: objective.value,
          }

          return [permitAction, repayAction]
        }

        return [
          ...getApproveActions({
            token: objective.reserve.token,
            spender: lendingPool,
            requiredValue: objective.requiredApproval,
            value: objective.value,
            context,
          }),
          repayAction,
        ]
      }

      case 'setUseAsCollateral': {
        const setUseAsCollateralAction: SetUseAsCollateralAction = {
          type: 'setUseAsCollateral',
          token: objective.token,
          useAsCollateral: objective.useAsCollateral,
        }
        return [setUseAsCollateralAction]
      }

      case 'setUserEMode': {
        const setUserEModeAction: SetUserEModeAction = {
          type: 'setUserEMode',
          eModeCategoryId: objective.eModeCategoryId,
        }
        return [setUserEModeAction]
      }

      case 'claimMarketRewards': {
        const ClaimMarketRewardsActions: ClaimMarketRewardsAction = {
          type: 'claimMarketRewards',
          token: objective.token,
          incentiveControllerAddress: objective.incentiveControllerAddress,
          assets: objective.assets,
        }

        return [ClaimMarketRewardsActions]
      }

      case 'upgradeSdai': {
        const approveActions = getApproveActions({
          token: objective.sdai,
          spender: getContractAddress(migrationActionsConfig.address, chainId),
          value: objective.amount,
          context,
        })
        const upgradeAction: UpgradeSdaiAction = {
          type: 'upgradeSdai',
          sdai: objective.sdai,
          susds: objective.susds,
          amount: objective.amount,
        }

        return [...approveActions, upgradeAction]
      }

      case 'unstake': {
        return [
          {
            type: 'unstake',
            stakingToken: objective.stakingToken,
            rewardToken: objective.rewardToken,
            amount: objective.amount,
            exit: objective.exit,
            farm: objective.farm,
          },
        ]
      }

      case 'withdrawFromSavings': {
        return createWithdrawFromSavingsActions(objective, context)
      }

      case 'depositToSavings': {
        return createDepositToSavingsActions(objective, context)
      }

      case 'upgradeSusdc': {
        return createUpgradeSusdcActions(objective, context)
      }

      case 'stake': {
        return [
          ...getApproveActions({
            token: objective.stakingToken,
            spender: objective.farm,
            value: objective.amount,
            context,
          }),
          {
            type: 'stake',
            stakingToken: objective.stakingToken,
            stakeAmount: objective.amount,
            rewardToken: objective.rewardToken,
            farm: objective.farm,
          },
        ]
      }

      case 'claimFarmRewards': {
        return [
          {
            type: 'claimFarmRewards',
            farm: objective.farm,
            rewardToken: objective.rewardToken,
            rewardAmount: objective.rewardAmount,
          },
        ]
      }

      case 'claimPointsRewards': {
        return [
          {
            type: 'claimPointsRewards',
            token: objective.token,
            epoch: objective.epoch,
            cumulativeAmount: objective.cumulativeAmount,
            merkleRoot: objective.merkleRoot,
            merkleProof: objective.merkleProof,
          },
        ]
      }

      case 'claimSparkRewards': {
        return [
          {
            type: 'claimSparkRewards',
            token: objective.token,
            epoch: objective.epoch,
            cumulativeAmount: objective.cumulativeAmount,
            merkleRoot: objective.merkleRoot,
            merkleProof: objective.merkleProof,
          },
        ]
      }

      case 'swap': {
        return createSwapActions(objective, context)
      }

      case 'stakeSpk': {
        const approveActions = getApproveActions({
          token: objective.spk,
          spender: getContractAddress(spkStakingModuleConfig.address, chainId),
          value: objective.amount,
          context,
        })
        const stakeSparkAction: StakeSpkAction = {
          type: 'stakeSpk',
          spk: objective.spk,
          amount: objective.amount,
        }
        return [...approveActions, stakeSparkAction]
      }

      case 'unstakeSpk': {
        return [
          {
            type: 'unstakeSpk',
            spk: objective.spk,
            amount: objective.amount,
            accountActiveShares: objective.accountActiveShares,
            unstakeAll: objective.unstakeAll,
          },
        ]
      }

      case 'finalizeSpkUnstake': {
        return [
          {
            spk: objective.spk,
            amount: objective.amount,
            type: 'finalizeSpkUnstake',
            epochs: objective.epochs,
          },
        ]
      }

      case 'claimAirdropRewards': {
        return [
          {
            type: 'claimAirdropRewards',
            source: objective.source,
            token: objective.token,
            epoch: objective.epoch,
            cumulativeAmount: objective.cumulativeAmount,
            merkleRoot: objective.merkleRoot,
            merkleProof: objective.merkleProof,
          },
        ]
      }

      case 'delegate': {
        return [{ type: 'delegate', delegate: objective.delegate, delegator: objective.delegator }]
      }

      case 'undelegate': {
        return [{ type: 'undelegate', delegator: objective.delegator }]
      }

      case 'depositToWeth': {
        return [{ type: 'depositToWeth', eth: objective.eth, value: objective.value }]
      }

      case 'withdrawFromWeth': {
        return [{ type: 'withdrawFromWeth', eth: objective.eth, amount: objective.amount }]
      }

      default:
        assertNever(objective)
    }
  })
}
